import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import QuoteRequestModal from "./QuoteRequestModal";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";

// The hero + content section of a main-service detail view (ServiceCard-driven). Extracted so it
// can be rendered both on its own page (/services/:id) and, hero-only via `bannerOnly`, stacked
// above a sub-service's own detail on that sub-service's page.
export default function ServiceDetailContent({ service, subServices, backTo = "/#services", backLabel = "Back to Services", bannerOnly = false }) {
  const [showQuote, setShowQuote] = useState(false);
  const [typedTitle, setTypedTitle] = useState("");

  // Typewriter effect: reveal the hero title one character at a time.
  useEffect(() => {
    const text = service.title || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [service.title]);

  // Only the full (non-banner) render owns the page title - a bannerOnly render is supplementary
  // to a SubServiceDetail below it, which sets its own title.
  useEffect(() => {
    if (!bannerOnly && service.title) {
      document.title = `${service.title} - Mr. Aali Online Technologies`;
    }
  }, [service.title, bannerOnly]);

  const icons = (service.icons || "").split(",").filter(Boolean);

  return (
    <section className="relative text-white bg-navy">
      {/* Compact hero: image 30% left, title + description 70% right - same background style as the Home hero */}
      <div className="relative py-10 border-b border-navy-border overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="floating-light absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "18s" }} />
          <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" style={{ animationDuration: "22s", animationDelay: "2s" }} />
          <div className="floating-light absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full bg-purple-500/10 blur-[100px]" style={{ animationDuration: "26s", animationDelay: "4s" }} />
          <div className="floating-light absolute bottom-10 left-10 w-[300px] h-[300px] rounded-full bg-accent-light/10 blur-[100px]" style={{ animationDuration: "20s", animationDelay: "1s" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link to={backTo} className="inline-block text-accent-light text-sm mb-4 hover:underline">
            &larr; {backLabel}
          </Link>

          <div className="grid md:grid-cols-[30%_70%] gap-8 items-center">
            <div className="rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
              {service.image ? (
                <img src={service.image} alt={service.title} className="w-full h-auto block bg-navy" />
              ) : (
                <div className="w-full h-40 md:h-48 bg-brand-gradient" />
              )}
            </div>

            <div>
              <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight">
                {typedTitle}
                <span className="typing-cursor inline-block w-[3px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
              </h1>
              {service.subtitle && (
                <p className="mt-3 text-gray-300">{service.subtitle}</p>
              )}

              {icons.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">
                    Technologies We Use
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {icons.map((key) => {
                      const entry = DEV_TOOL_ICON_MAP[key];
                      if (!entry) return null;
                      const { Icon, label } = entry;
                      return (
                        <span
                          key={key}
                          title={label}
                          className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm"
                        >
                          <Icon size={16} />
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!bannerOnly && (
        <div className="relative py-12 md:py-16">
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-[60%_40%] gap-12 items-start">
              {/* Left column: content */}
              <div>
                <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight">{service.title}</h1>
                {service.subtitle && (
                  <p className="mt-4 text-lg text-gray-300">{service.subtitle}</p>
                )}

                {service.description && (
                  <div
                    className="mt-8 text-gray-300 leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-8 [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_a]:text-accent-light [&_strong]:text-white"
                    dangerouslySetInnerHTML={{ __html: service.description }}
                  />
                )}

                <button
                  onClick={() => setShowQuote(true)}
                  className="mt-8 px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-md shadow-accent/20"
                >
                  Request Quote &amp; Information
                </button>
              </div>

              {/* Right column: image + sub-services */}
              <div className="md:sticky md:top-24">
                <div className="relative rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_25px_rgba(34,211,238,0.2)]">
                  {service.image ? (
                    <img src={service.image} alt={service.title} className="w-full h-auto block bg-navy" />
                  ) : (
                    <div className="w-full h-72 md:h-80 bg-brand-gradient" />
                  )}
                </div>

                {subServices.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">
                      Sub Services
                    </p>
                    <div className="flex flex-col gap-3">
                      {subServices.map((sub, i) => (
                        <motion.div
                          key={sub.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: i * 0.08 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <Link
                            to={`/${sub.slug}`}
                            className="block w-full px-4 py-3 rounded-md bg-white/5 border border-navy-border text-gray-200 text-sm hover:border-accent-light hover:text-accent-light transition-colors"
                          >
                            {sub.title}
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!bannerOnly && showQuote && (
        <QuoteRequestModal serviceTitle={service.title} onClose={() => setShowQuote(false)} />
      )}
    </section>
  );
}
